import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import {
  api, ApiError, AdminQuestionDetail as Detail, AnswerResult,
  DistractorRationales, RichRationale, ServedQuestion,
} from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { renderQuestionContent, WorkedSolutionText } from "../lib/render-question";
import {
  ParsedMultiPart, parseMultiPartStem,
} from "../lib/multiPart";
import {
  AnswerInput, Solution, canCommit, composeAnswer,
} from "./Practice";

export default function AdminQuestionDetailPage() {
  const { me } = useAuth();
  const { qid } = useParams<{ qid: string }>();
  const [params] = useSearchParams();
  const includeDemo = params.get("include_demo") === "1";
  const [detail, setDetail] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!qid) return;
    setDetail(null);
    setError(null);
    api
      .adminQuestionDetail(parseInt(qid, 10), includeDemo)
      .then(setDetail)
      .catch((e) => setError(e?.status === 404 ? "Question not found." : "Could not load detail."));
  }, [qid, includeDemo]);

  if (!me) return null;
  if (!me.signed_in) return <Navigate to="/login" replace />;
  if (!me.is_admin) return <Navigate to="/" replace />;

  if (error) {
    return (
      <div className="page page--wide">
        <BackLink includeDemo={includeDemo} />
        <p className="muted" style={{ color: "var(--negative)" }}>{error}</p>
      </div>
    );
  }
  if (!detail) {
    return (
      <div className="page page--wide">
        <BackLink includeDemo={includeDemo} />
        <p className="muted">Loading…</p>
      </div>
    );
  }

  const q = detail.question;
  const s = detail.stats;

  return (
    <div className="page page--wide">
      <div className="stack-lg">
        <BackLink includeDemo={includeDemo} />

        <header>
          <div className="qd__crumb">
            {q.topic_name && <span>{q.topic_name} ›</span>} <span>{q.concept_name}</span>
          </div>
          <h1 className="h1" style={{ marginTop: 4, marginBottom: 8 }}>
            Question <span className="qd__qid">#{q.id}</span>
          </h1>
          <div className="qd__meta">
            <span className="qd__badge">{fmtRf(q.response_format)}</span>
            <span className="qd__badge qd__badge--muted">{q.difficulty}</span>
            <StatusBadge status={q.status} />
            {q.cognitive_type && (
              <span className="qd__badge qd__badge--muted">{q.cognitive_type}</span>
            )}
            {q.variety_axis && (
              <span className="qd__badge qd__badge--muted">{q.variety_axis}</span>
            )}
            {q.sub_skill_id && (
              <span className="qd__badge qd__badge--muted">
                sub-skill: {q.sub_skill_id}
              </span>
            )}
            {q.bank_source && (
              <span className="qd__bank-source">{q.bank_source}</span>
            )}
          </div>
          <StatusToggle
            qid={q.id}
            status={q.status}
            onChanged={(newStatus) =>
              setDetail((d) =>
                d ? { ...d, question: { ...d.question, status: newStatus } } : d,
              )
            }
          />
        </header>

        <div className="stat-grid">
          <Stat label="Served" value={String(s.served)} />
          <Stat label="Answered" value={String(s.answered)} />
          <Stat label="% correct" value={fmtPct(s.pct_correct)} />
          <Stat label="Skip rate" value={fmtPct(s.skip_rate)} />
          <Stat label="Avg time" value={fmtTime(s.avg_time_ms)} />
          <Stat label="Votes" value={`▲${s.upvotes}  ▼${s.downvotes}`} />
        </div>

        <TryThisQuestionPreview qid={q.id} />

        <div className="card stack-lg">
          <section>
            <h2 className="qd__h2">Question</h2>
            <div className="question-text qd__question">{renderQuestionContent(q.question)}</div>
            {q.choices && q.choices.length > 0 && (
              <ol className="qd__choices">
                {q.choices.map((c, i) => {
                  const letter = String.fromCharCode(65 + i);
                  const text = c.replace(/^[A-D]\)\s*/, "");
                  return (
                    <li key={letter}>
                      <span className="qd__choice-letter">{letter})</span>
                      <span>{text}</span>
                    </li>
                  );
                })}
              </ol>
            )}
          </section>

          <section>
            <h2 className="qd__h2">Answer key</h2>
            <div className="qd__answer-key">{q.answer_key}</div>
          </section>

          <section>
            <h2 className="qd__h2">Worked solution</h2>
            <div className="qd__worked">
              <WorkedSolutionText text={q.worked_solution} />
            </div>
          </section>

          {q.parts && q.parts.length > 0 && (
            <section>
              <h2 className="qd__h2">Structured parts</h2>
              <ul className="qd__rationales">
                {q.parts.map((p) => (
                  <li key={p.label} style={{ marginBottom: 12 }}>
                    <div style={{ fontWeight: 600 }}>
                      ({p.label}) <span className="muted">{p.type}</span>
                    </div>
                    <div style={{ marginBottom: 6 }}>{p.prompt}</div>
                    <div style={{ fontSize: 13 }}>
                      <span className="muted">Answer:</span>{" "}
                      <code>{p.answer}</code>
                      {p.type === "numeric" && p.tolerance !== undefined && (
                        <>
                          {" "}
                          <span className="muted">(±{p.tolerance} absolute)</span>
                        </>
                      )}
                    </div>
                    {p.type === "choice" && p.choices && (
                      <div style={{ fontSize: 13 }}>
                        <span className="muted">Choices:</span>{" "}
                        {p.choices.join(" · ")}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {q.distractor_rationales && (
            <section>
              <h2 className="qd__h2">Distractor rationales</h2>
              <ul className="qd__rationales">
                {renderRationales(q.distractor_rationales)}
              </ul>
            </section>
          )}

          {q.assumptions && q.assumptions.length > 0 && (
            <section>
              <h2 className="qd__h2">Assumptions</h2>
              <ul className="qd__assumptions">
                {q.assumptions.map((a, i) => (
                  <li key={i}>{a}</li>
                ))}
              </ul>
            </section>
          )}
        </div>

        <div className="card stack" style={{ padding: 0 }}>
          <div className="card__section-head">
            Per-student attempts ({detail.attempts.length})
          </div>
          {detail.attempts.length === 0 ? (
            <div className="qd__empty">No attempts yet.</div>
          ) : (
            <div className="admin__table-wrap">
              <table className="admin__table">
                <thead>
                  <tr>
                    <th>BlazerID</th>
                    <th>Correct?</th>
                    <th>What they answered</th>
                    <th>Time</th>
                    <th>When</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.attempts.map((a, i) => (
                    <tr key={i}>
                      <td>
                        <span className="admin__id">{a.blazerid ?? "(unknown)"}</span>
                        {a.blazerid?.startsWith("demo_") && (
                          <span className="admin__badge admin__badge--demo">seed</span>
                        )}
                      </td>
                      <td>
                        <span className={`qd__verdict qd__verdict--${a.correct ? "ok" : "miss"}`}>
                          {a.correct ? "✓ Correct" : "✗ Incorrect"}
                        </span>
                      </td>
                      <td className="qd__answer-cell">{a.answer ?? "(no text captured)"}</td>
                      <td className="num">{fmtTime(a.time_ms)}</td>
                      <td className="num">{fmtRelative(a.answered_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {detail.comments.length > 0 && (
          <div className="card stack" style={{ padding: 0 }}>
            <div className="card__section-head">Comments on this question</div>
            <ul className="admin__comments">
              {detail.comments.map((c) => (
                <li key={c.id} className="admin__comment">
                  <div className="admin__comment__head">
                    <span className="admin__id">{c.blazerid ?? "(unknown)"}</span>
                    <span className="admin__crumb">{fmtRelative(c.created_at)}</span>
                  </div>
                  <div className="admin__comment__body">{c.body}</div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const isActive = status === "active";
  return (
    <span
      className={`qd__badge ${isActive ? "qd__badge--muted" : ""}`}
      style={
        isActive
          ? undefined
          : { background: "var(--negative, #c33)", color: "#fff", borderColor: "transparent" }
      }
    >
      status: {status}
    </span>
  );
}

function StatusToggle({
  qid,
  status,
  onChanged,
}: {
  qid: number;
  status: string;
  onChanged: (newStatus: string) => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const isActive = status === "active";
  const next: "active" | "retired" = isActive ? "retired" : "active";
  const label = isActive ? "Retire question" : "Reactivate question";

  async function flip() {
    if (
      isActive &&
      !window.confirm(
        "Retire this question? It will stop being served immediately. You can reactivate it later.",
      )
    ) {
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      const r = await api.adminSetQuestionStatus(qid, next);
      onChanged(r.new_status);
    } catch (e) {
      if (e instanceof ApiError) {
        setErr(`Failed (${e.status}).`);
      } else {
        setErr("Failed.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center" }}>
      <button
        type="button"
        className="btn"
        onClick={flip}
        disabled={submitting}
        style={
          isActive
            ? {
                background: "var(--negative, #c33)",
                color: "#fff",
                borderColor: "var(--negative, #c33)",
              }
            : undefined
        }
      >
        {submitting ? "Saving…" : label}
      </button>
      {err && (
        <span className="muted" style={{ color: "var(--negative, #c33)" }}>
          {err}
        </span>
      )}
    </div>
  );
}

function BackLink({ includeDemo }: { includeDemo: boolean }) {
  return (
    <Link
      to={`/admin?tab=bank${includeDemo ? "&include_demo=1" : ""}`}
      className="qd__back"
    >
      ← Back to bank quality
    </Link>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="stat-card">
      <div className="stat-card__label">{label}</div>
      <div className="stat-card__value">{value}</div>
    </div>
  );
}

function renderRationales(dr: DistractorRationales) {
  if (!dr) return null;
  const entries: { label: string; r: RichRationale | { raw: string } }[] = [];
  if (Array.isArray(dr)) {
    dr.forEach((entry, i) => {
      const label = String.fromCharCode(65 + i);
      if (typeof entry === "string") entries.push({ label, r: { raw: entry } });
      else entries.push({ label, r: entry as RichRationale });
    });
  } else {
    Object.entries(dr).forEach(([k, v]) => {
      if (typeof v === "string") entries.push({ label: k, r: { raw: v } });
      else entries.push({ label: k, r: v as RichRationale });
    });
  }
  return entries.map((e) => (
    <li key={e.label}>
      <div className="qd__rationale-label">{e.label})</div>
      {"raw" in e.r ? (
        <div>{e.r.raw}</div>
      ) : (
        <>
          {e.r.why_picked && (
            <div className="qd__rationale-row">
              <span className="qd__rationale-tag">Why a student might pick it</span>
              <div>{e.r.why_picked}</div>
            </div>
          )}
          {e.r.why_wrong && (
            <div className="qd__rationale-row">
              <span className="qd__rationale-tag">Why it's wrong</span>
              <div>{e.r.why_wrong}</div>
            </div>
          )}
        </>
      )}
    </li>
  ));
}

function fmtPct(v: number | null): string {
  if (v === null || v === undefined) return "-";
  return `${Math.round(v * 100)}%`;
}
function fmtTime(ms: number | null): string {
  if (ms === null || ms === undefined) return "-";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
}
function fmtRelative(iso: string | null): string {
  if (!iso) return "-";
  const t = new Date(iso.replace(" ", "T") + "Z").getTime();
  if (Number.isNaN(t)) return iso;
  const dMs = Date.now() - t;
  const min = Math.round(dMs / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.round(hr / 24)}d ago`;
}
function fmtRf(rf: string): string {
  if (rf === "multiple_choice") return "Multiple choice";
  if (rf === "single_numeric") return "Single numeric";
  if (rf === "multi_part") return "Multi-part";
  if (rf === "free_text") return "Free text";
  return rf;
}

// ---------------------------------------------------------------------------
// "Try this question" — inline student-facing preview on the admin detail page.
//
// Why this component exists. The student practice path picks via
// weighted-random over a scored candidate pool, so the same question
// rarely surfaces on repeated tries (seven serves failed to land fixture
// q641 during a visual-review session). This preview is the targeted
// bypass: it fetches the same ServedQuestion shape /api/practice/next
// returns and reuses the AnswerInput + Solution components Practice.tsx
// exports, so what an admin sees here is byte-identical to what a
// student would see live. Grading goes through the real grader on the
// backend; only the events writer is bypassed so admin previews don't
// pollute pilot stats (no `served`, no `answer_committed`, no
// `answer_correct/incorrect`).
// ---------------------------------------------------------------------------
function TryThisQuestionPreview({ qid }: { qid: number }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState<ServedQuestion | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [chosenLetter, setChosenLetter] = useState<string | null>(null);
  const [partAnswers, setPartAnswers] = useState<string[]>([]);
  const [result, setResult] = useState<AnswerResult | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Same parts synthesis Practice.tsx uses: prefer parts_json when present,
  // fall back to marker-prose parse for legacy multi_part.
  const parsedParts = useMemo<ParsedMultiPart | null>(() => {
    if (!q || q.response_format !== "multi_part") return null;
    if (q.parts && q.parts.length > 0) {
      return {
        preamble: q.question,
        parts: q.parts.map((p) => {
          if (p.type === "numeric") {
            return {
              letter: p.label, prompt: p.prompt,
              type: { kind: "numeric" as const },
              confidence: "known" as const,
            };
          }
          if (p.type === "choice") {
            return {
              letter: p.label, prompt: p.prompt,
              type: { kind: "choice" as const, options: (p.choices ?? []).slice(0, 4) },
              confidence: "known" as const,
            };
          }
          return {
            letter: p.label, prompt: p.prompt,
            type: { kind: "text" as const },
            confidence: "known" as const,
          };
        }),
      };
    }
    return parseMultiPartStem(q.question);
  }, [q]);

  function reset() {
    setDraft("");
    setChosenLetter(null);
    setPartAnswers([]);
    setResult(null);
  }

  async function load() {
    setLoadErr(null);
    setQ(null);
    reset();
    try {
      const next = await api.adminQuestionPreview(qid);
      setQ(next);
      if (next.response_format === "multi_part" && next.parts) {
        setPartAnswers(new Array(next.parts.length).fill(""));
      } else {
        setPartAnswers([""]);
      }
    } catch (e) {
      const msg = e instanceof ApiError
        ? `Could not load preview (${e.status}).`
        : "Could not load preview.";
      setLoadErr(msg);
    }
  }

  async function submit() {
    if (!q) return;
    const hasStructured = !!(q.parts && q.parts.length > 0);
    const ans = composeAnswer(q.response_format, parsedParts,
                              { draft, chosenLetter, partAnswers },
                              hasStructured);
    if (!ans) return;
    setSubmitting(true);
    try {
      const r = await api.adminQuestionPreviewGrade(qid, ans);
      setResult(r);
    } catch {
      // Surface a minimal failure indicator; this is admin-only and rare.
      setResult({
        correct: false, score: 0, answer_key: "",
        worked_solution: "Preview grading failed.",
        choices: null, distractor_rationales: null,
        method: "preview_error", part_results: null,
      } as AnswerResult);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="card stack" style={{ borderStyle: "dashed" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div>
          <h2 className="qd__h2" style={{ margin: 0 }}>Try this question</h2>
          <p className="muted" style={{ margin: "4px 0 0", fontSize: 13 }}>
            Renders Q#{qid} through the student components. Graded via the real
            endpoint; no events recorded (preview ≠ pilot data).
          </p>
        </div>
        {!open ? (
          <button
            type="button" className="btn"
            onClick={() => { setOpen(true); load(); }}
          >
            Open preview
          </button>
        ) : (
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button" className="btn"
              onClick={() => { reset(); load(); }}
            >
              Reset
            </button>
            <button
              type="button" className="btn btn--ghost"
              onClick={() => { setOpen(false); reset(); setQ(null); }}
            >
              Close
            </button>
          </div>
        )}
      </div>

      {open && (
        <div className="stack" style={{ marginTop: 8 }}>
          {loadErr && <p className="muted" style={{ color: "var(--negative)" }}>{loadErr}</p>}
          {!q && !loadErr && <p className="muted">Loading…</p>}
          {q && (
            <>
              <div className="question-text">{renderQuestionContent(q.question)}</div>
              <AnswerInput
                q={q}
                parsedParts={parsedParts}
                disabled={!!result || submitting}
                draft={draft}
                setDraft={setDraft}
                chosenLetter={chosenLetter}
                setChosenLetter={setChosenLetter}
                partAnswers={partAnswers}
                setPartAnswers={setPartAnswers}
                graded={result}
              />
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button
                  type="button" className="btn btn--primary"
                  disabled={
                    submitting || !!result ||
                    !canCommit(q.response_format, parsedParts,
                               { draft, chosenLetter, partAnswers })
                  }
                  onClick={submit}
                >
                  {submitting ? "Grading…" : "Submit (preview)"}
                </button>
                <span className="muted" style={{ fontSize: 12 }}>
                  No events recorded. Preview only.
                </span>
              </div>
              {result && <Solution result={result} q={q} />}
            </>
          )}
        </div>
      )}
    </div>
  );
}
